import React, { useEffect, useRef, useState } from "react";
import { Input, Button } from "antd";
import { get } from "../../libs/helpers/ioc";
import Localize, {
  l,
  getCurrentLocale,
  formatNumber,
} from "../../libs/localization";
import { filtersToTextApi, textToFiltersApi } from "../../api/agent.api";
import { splitIncomingFilters } from "../../hooks/useCompanySearch";
import ErrorModal from "../ErrorModal";
import "./FiltersSummary.scss";

const DEBOUNCE_MS = 800;

const TRY_EXAMPLE_KEYS = [
  "PAGES.COMPANIES.FILTERS_TO_TEXT.EXAMPLE_1",
  "PAGES.COMPANIES.FILTERS_TO_TEXT.EXAMPLE_2",
  "PAGES.COMPANIES.FILTERS_TO_TEXT.EXAMPLE_3",
];

const Sparkle = ({ small }) => (
  <div
    className={`filtersSummary__sparkle${
      small ? " filtersSummary__sparkle--sm" : ""
    }`}
  >
    <span className="filtersSummary__sparkle-main" />
    {!small && <span className="filtersSummary__sparkle-gold" />}
    {!small && <span className="filtersSummary__sparkle-accent" />}
  </div>
);

// Shows the currently-applied Company Search filters as a plain-language
// sentence, and lets the user hand-edit that sentence in place to change the
// filters instead of hunting through the form. Only rendered for users with
// the same "agent" scope that gates the chat feature, since it uses the same
// backend endpoints/permission.
const FiltersSummary = ({
  activeFilters,
  setDataForm,
  setCity,
  setBrandAdvertiserFilters,
  matchCount,
  companyNames,
}) => {
  const permissions = get("scopes") || {};
  const hasAgentPermission = !!permissions.agent;

  const [description, setDescription] = useState("");
  const [loadingDescription, setLoadingDescription] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState("");
  const [applying, setApplying] = useState(false);
  const [unsupported, setUnsupported] = useState([]);

  const lastSerializedRef = useRef(null);
  const skipNextFetchRef = useRef(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!hasAgentPermission || editing) return undefined;

    const serialized = JSON.stringify(activeFilters);
    if (serialized === lastSerializedRef.current) return undefined;

    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      lastSerializedRef.current = serialized;
      return undefined;
    }

    const timeout = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      setLoadingDescription(true);
      try {
        const res = await filtersToTextApi({
          filters: activeFilters,
          companyNames,
          locale: getCurrentLocale(),
        });
        if (requestId === requestIdRef.current) {
          lastSerializedRef.current = serialized;
          setDescription(res.text);
          setUnsupported([]);
        }
      } catch (e) {
        // Cosmetic side feature: fail silently (no popup), just hide the stale summary
        if (requestId === requestIdRef.current) setDescription("");
      } finally {
        if (requestId === requestIdRef.current) setLoadingDescription(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters, editing, hasAgentPermission, companyNames]);

  if (!hasAgentPermission) return null;

  const startEditing = () => {
    setDraftText(description);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setUnsupported([]);
  };

  const applyEdit = async () => {
    setApplying(true);
    try {
      const res = await textToFiltersApi({
        filters: activeFilters,
        originalText: description,
        editedText: draftText,
        locale: getCurrentLocale(),
      });
      const { dataForm, brandAdvertiserFilters } = splitIncomingFilters(
        res.filters,
      );
      skipNextFetchRef.current = true;
      setDataForm(dataForm);
      setBrandAdvertiserFilters(brandAdvertiserFilters || []);
      // res.filters is the complete, absolute filter set (per textToFilters.md's
      // prompt), not a delta — so the separately-tracked city autocomplete value
      // must be cleared too, or it would silently override dataForm.city (or
      // reassert a city the edit removed) via getParsedDataForm's own override.
      setCity("");
      setDescription(res.text);
      setUnsupported(res.unsupported || []);
      setEditing(false);
    } catch (e) {
      ErrorModal.show(e);
    } finally {
      setApplying(false);
    }
  };

  const mode = editing ? "edit" : applying ? "interpreting" : "view";
  const syncing = loadingDescription || applying;

  return (
    <div className="filtersSummary">
      <div className="filtersSummary__header">
        <Sparkle />
        <span className="filtersSummary__label">
          {l("PAGES.COMPANIES.FILTERS_TO_TEXT.LABEL")}
        </span>
        <span className="filtersSummary__pill">AI</span>
        {typeof matchCount === "number" && (
          <span className="filtersSummary__count">
            {Localize("PAGES.COMPANIES.FILTERS_TO_TEXT.MATCHES", [
              formatNumber(matchCount),
            ])}
          </span>
        )}
      </div>

      <div
        className={`filtersSummary__ring${
          syncing ? " filtersSummary__ring--syncing" : ""
        }`}
      >
        {syncing && (
          <div className="filtersSummary__shimmer-wrap">
            <div className="filtersSummary__shimmer" />
          </div>
        )}
        <div className="filtersSummary__surface">
          {mode === "edit" && (
            <div className="filtersSummary__edit">
              <Input.TextArea
                className="filtersSummary__textarea"
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                placeholder={l("PAGES.COMPANIES.FILTERS_TO_TEXT.PLACEHOLDER")}
                rows={2}
                autoFocus
              />
              <div className="filtersSummary__editActions">
                <Button
                  className="filtersSummary__applyBtn"
                  onClick={applyEdit}
                >
                  <span className="filtersSummary__applyGlyph" />
                  {l("PAGES.COMPANIES.FILTERS_TO_TEXT.APPLY")}
                </Button>
                <Button
                  className="filtersSummary__cancelBtn"
                  onClick={cancelEditing}
                >
                  {l("COMMON.CANCEL")}
                </Button>
                <span className="filtersSummary__helper">
                  {l("PAGES.COMPANIES.FILTERS_TO_TEXT.HELPER")}
                </span>
              </div>
              <div className="filtersSummary__tryRow">
                <span className="filtersSummary__tryLabel">
                  {l("PAGES.COMPANIES.FILTERS_TO_TEXT.TRY")}
                </span>
                {TRY_EXAMPLE_KEYS.map((key) => {
                  const example = l(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      className="filtersSummary__tryChip"
                      onClick={() => setDraftText(example)}
                    >
                      {example}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {mode === "interpreting" && (
            <div className="filtersSummary__interpreting">
              <Sparkle small />
              <span className="filtersSummary__interpretingText">
                {l("PAGES.COMPANIES.FILTERS_TO_TEXT.INTERPRETING")}
              </span>
              <span className="filtersSummary__dots">
                <span className="filtersSummary__dot" />
                <span className="filtersSummary__dot" />
                <span className="filtersSummary__dot" />
              </span>
            </div>
          )}

          {mode === "view" && (
            <div className="filtersSummary__view">
              <div className="filtersSummary__sentence" key={description}>
                {description}
              </div>
              <Button
                className="filtersSummary__editBtn"
                onClick={startEditing}
              >
                <span className="filtersSummary__editGlyph" />
                {l("PAGES.COMPANIES.FILTERS_TO_TEXT.EDIT")}
              </Button>
            </div>
          )}
        </div>
      </div>

      {unsupported.length > 0 && (
        <div className="filtersSummary__dropped">
          <span className="filtersSummary__droppedBadge">!</span>
          <div className="filtersSummary__droppedBody">
            <b className="filtersSummary__droppedTitle">
              {l("PAGES.COMPANIES.FILTERS_TO_TEXT.DROPPED_TITLE")}
            </b>{" "}
            {l("PAGES.COMPANIES.FILTERS_TO_TEXT.DROPPED_COULD_NOT_MAP")}{" "}
            {unsupported.map((item) => (
              <span key={item} className="filtersSummary__droppedChip">
                "{item}"
              </span>
            ))}{" "}
            {l("PAGES.COMPANIES.FILTERS_TO_TEXT.DROPPED_SUFFIX")}
          </div>
          <button
            type="button"
            className="filtersSummary__droppedDismiss"
            onClick={() => setUnsupported([])}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default FiltersSummary;
